// Admin-only page to CRUD patients and assign nurses.
// Patients are referenced by name in the program editor, which auto-loads the
// medication catalog (also keyed by patient_name).

import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Loader2, UserPlus, Pencil, Trash2, User2, MapPin, Users, Check, FileText, FileDown, Pill,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { exportPatientTxt } from "@/lib/txt-export";
import MedicationCatalogDialog from "@/components/MedicationCatalogDialog";

// Relative "hace X" label from an ISO timestamp (es-MX friendly).
function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "";
  if (ms < 60000) return "hace un momento";
  const min = Math.floor(ms / 60000);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d} dia${d === 1 ? "" : "s"}`;
  const mo = Math.floor(d / 30);
  return `hace ${mo} mes${mo === 1 ? "" : "es"}`;
}

const empty = { name: "", room: "", bed: "", notes: "", assigned_nurse_ids: [], inactive_nurse_ids: [], peritoneal_dialysis_active: false, gastrostomy_active: false };

export default function PatientsAdminPage() {
  const nav = useNavigate();
  const [patients, setPatients] = useState(null);
  const [nurses, setNurses] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [medsPatient, setMedsPatient] = useState(null);
  const [medCatalog, setMedCatalog] = useState([]);

  const reloadMeds = useCallback(async () => {
    if (!medsPatient) return;
    try {
      const { data } = await api.get("/patient-meds", {
        params: { patient_name: medsPatient.name },
      });
      setMedCatalog(Array.isArray(data) ? data : []);
    } catch {
      setMedCatalog([]);
    }
  }, [medsPatient]);

  useEffect(() => {
    reloadMeds();
  }, [reloadMeds]);

  const exportTxt = async (p) => {
    setExportingId(p.id);
    try {
      const { data } = await api.get("/programs");
      const count = exportPatientTxt(data, p.name);
      if (!count) toast.info("Este paciente no tiene programas registrados");
      else toast.success(`Respaldo TXT generado (${count} programa${count === 1 ? "" : "s"})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el respaldo");
    } finally {
      setExportingId(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [pRes, uRes, mRes] = await Promise.all([
        api.get("/patients"),
        api.get("/admin/users"),
        api.get("/patients/metrics"),
      ]);
      setPatients(Array.isArray(pRes.data) ? pRes.data : []);
      const onlyNurses = (uRes.data || []).filter((u) => u.role === "nurse");
      setNurses(onlyNurses);
      setMetrics(mRes.data && typeof mRes.data === "object" ? mRes.data : {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando pacientes");
      setPatients([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const nurseById = useMemo(() => {
    const map = {};
    for (const n of nurses) map[n.id] = n;
    return map;
  }, [nurses]);

  const openNew = () => {
    setEditingId(null);
    setDraft(empty);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setDraft({
      name: p.name,
      room: p.room || "",
      bed: p.bed || "",
      notes: p.notes || "",
      assigned_nurse_ids: p.assigned_nurse_ids || [],
      inactive_nurse_ids: p.inactive_nurse_ids || [],
      peritoneal_dialysis_active: !!p.peritoneal_dialysis_active,
      gastrostomy_active: !!p.gastrostomy_active,
    });
    setDialogOpen(true);
  };

  // Toggle the assignment state for a nurse. There are 3 visual states:
  //   * Activa  -> in assigned_nurse_ids, NOT in inactive_nurse_ids
  //   * Inactiva-> in assigned_nurse_ids AND in inactive_nurse_ids (history kept)
  //   * Sin     -> not in assigned_nurse_ids
  const toggleNurse = (nid) => {
    setDraft((d) => {
      const assigned = d.assigned_nurse_ids;
      const inactive = d.inactive_nurse_ids;
      if (!assigned.includes(nid)) {
        // None -> Activa
        return { ...d, assigned_nurse_ids: [...assigned, nid] };
      }
      if (!inactive.includes(nid)) {
        // Activa -> Inactiva (keep in assigned for history)
        return { ...d, inactive_nurse_ids: [...inactive, nid] };
      }
      // Inactiva -> remove completely
      return {
        ...d,
        assigned_nurse_ids: assigned.filter((x) => x !== nid),
        inactive_nurse_ids: inactive.filter((x) => x !== nid),
      };
    });
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Captura el nombre del paciente");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/patients/${editingId}`, draft);
        toast.success("Paciente actualizado");
      } else {
        await api.post("/patients", draft);
        toast.success("Paciente registrado");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/patients/${deleteTarget.id}`);
      toast.success("Paciente eliminado");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AppHeader subtitle="Pacientes" />
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => nav("/dashboard")}
            className="h-9 gap-1 px-2 text-sm font-semibold text-slate-700"
            data-testid="patients-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al panel
          </Button>
          <Button
            onClick={openNew}
            className="h-10 rounded-xl bg-[#1D4ED8] px-4 font-semibold hover:bg-[#1E40AF]"
            data-testid="patients-new-btn"
          >
            <UserPlus className="mr-2 h-4 w-4" /> Nuevo paciente
          </Button>
        </div>

        {patients === null ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
          </div>
        ) : patients.length === 0 ? (
          <div
            className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center"
            data-testid="patients-empty"
          >
            <User2 className="mx-auto mb-2 h-10 w-10 text-slate-300" />
            <h2 className="text-lg font-bold text-slate-900">Sin pacientes registrados</h2>
            <p className="mt-1 text-sm text-slate-500">
              Registra al primer paciente y asignalo a las enfermeras responsables.
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="patients-list">
            {patients.map((p) => (
              <li
                key={p.id}
                className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
                data-testid={`patient-row-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900">{p.name}</p>
                    {(p.room || p.bed) && (
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                        <MapPin className="h-3 w-3" />
                        {p.room && p.room}{p.room && p.bed && " #"}{!p.room && p.bed && "#"}{p.bed && p.bed}
                      </p>
                    )}
                    {p.peritoneal_dialysis_active && (
                      <span
                        className="mt-1 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-800"
                        data-testid={`patient-pd-badge-${p.id}`}
                      >
                        DPCA activa
                      </span>
                    )}
                    {p.gastrostomy_active && (
                      <span
                        className="ml-1 mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800"
                        data-testid={`patient-peg-badge-${p.id}`}
                      >
                        PEG activa
                      </span>
                    )}
                    {p.notes && (
                      <p className="mt-1 text-xs italic text-slate-600">{p.notes}</p>
                    )}
                    {(() => {
                      const m = metrics[(p.name || "").trim()];
                      return (
                        <p
                          className="mt-1.5 flex items-center gap-1 text-xs text-slate-600"
                          data-testid={`patient-metrics-${p.id}`}
                        >
                          <FileText className="h-3 w-3 flex-shrink-0 text-slate-400" />
                          {m && m.count > 0 ? (
                            <>
                              <span className="font-bold text-slate-800">{m.count}</span>
                              &nbsp;programa{m.count === 1 ? "" : "s"}
                              {m.last_activity && (
                                <span className="text-slate-500">
                                  &nbsp;· ultimo {relTime(m.last_activity)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="italic text-slate-400">Sin programas aun</span>
                          )}
                        </p>
                      );
                    })()}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <Users className="h-3.5 w-3.5 text-slate-400" />
                      {(p.assigned_nurse_ids || []).length === 0 ? (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                          Sin asignar
                        </span>
                      ) : (
                        (p.assigned_nurse_ids || []).map((nid) => {
                          const inactive = (p.inactive_nurse_ids || []).includes(nid);
                          return (
                            <span
                              key={nid}
                              className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                inactive
                                  ? "bg-amber-100 text-amber-800 line-through"
                                  : "bg-green-100 text-green-800"
                              }`}
                              title={inactive ? "Inactiva (historial conservado)" : "Activa"}
                            >
                              {nurseById[nid]?.name || nurseById[nid]?.email || "(?)"}
                              {inactive ? " - inactiva" : ""}
                            </span>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 hover:bg-emerald-50"
                      onClick={() => setMedsPatient(p)}
                      title="Catalogo de medicamentos autorizados"
                      data-testid={`patient-meds-${p.id}`}
                    >
                      <Pill className="h-4 w-4 text-emerald-700" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 hover:bg-blue-50"
                      onClick={() => exportTxt(p)}
                      disabled={exportingId === p.id}
                      title="Descargar respaldo TXT de todos sus programas"
                      data-testid={`patient-export-${p.id}`}
                    >
                      {exportingId === p.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-700" />
                      ) : (
                        <FileDown className="h-4 w-4 text-blue-700" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => openEdit(p)}
                      data-testid={`patient-edit-${p.id}`}
                    >
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 hover:bg-red-50"
                      onClick={() => setDeleteTarget(p)}
                      data-testid={`patient-delete-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {/* Dialog create / edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="patient-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar paciente" : "Registrar nuevo paciente"}
            </DialogTitle>
            <DialogDescription>
              El nombre se enlaza al catalogo de medicamentos y al historial.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">
                Nombre <span className="text-red-600">*</span>
              </Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Ej. Juan Perez Lopez"
                className="h-10 text-base"
                data-testid="patient-input-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Calle</Label>
                <Input
                  value={draft.room}
                  onChange={(e) => setDraft({ ...draft, room: e.target.value })}
                  placeholder="Av. Juarez"
                  className="h-10 text-base"
                  data-testid="patient-input-room"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-500">Numero</Label>
                <Input
                  value={draft.bed}
                  onChange={(e) => setDraft({ ...draft, bed: e.target.value })}
                  placeholder="152"
                  className="h-10 text-base"
                  data-testid="patient-input-bed"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">Notas</Label>
              <Textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Diagnostico, alergias, observaciones generales..."
                rows={2}
                className="text-base"
                data-testid="patient-input-notes"
              />
            </div>
            <div>
              <label
                htmlFor="pd-toggle"
                className="flex cursor-pointer items-start gap-3 rounded-xl border-2 border-slate-200 bg-white p-3 transition-colors hover:border-[#1D4ED8]"
                data-testid="patient-pd-toggle-wrap"
              >
                <input
                  id="pd-toggle"
                  type="checkbox"
                  checked={!!draft.peritoneal_dialysis_active}
                  onChange={(e) =>
                    setDraft({ ...draft, peritoneal_dialysis_active: e.target.checked })
                  }
                  className="mt-1 h-4 w-4 accent-[#1D4ED8]"
                  data-testid="patient-pd-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold text-slate-900">Dialisis Peritoneal (DPCA)</div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    Habilita la bitacora de recambios, peso matutino y balance de ultrafiltracion 24h en el programa de cuidados.
                  </p>
                </div>
              </label>
            </div>
            <div>
              <label
                htmlFor="peg-toggle"
                className="flex cursor-pointer items-start gap-3 rounded-xl border-2 border-slate-200 bg-white p-3 transition-colors hover:border-amber-500"
                data-testid="patient-peg-toggle-wrap"
              >
                <input
                  id="peg-toggle"
                  type="checkbox"
                  checked={!!draft.gastrostomy_active}
                  onChange={(e) =>
                    setDraft({ ...draft, gastrostomy_active: e.target.checked })
                  }
                  className="mt-1 h-4 w-4 accent-amber-600"
                  data-testid="patient-peg-toggle"
                />
                <div className="flex-1">
                  <div className="text-sm font-bold text-slate-900">Gastrostomia (PEG)</div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    Habilita la bitacora de tomas por sonda, cuidados del estoma y acumulado enteral 24h en el programa de cuidados.
                  </p>
                </div>
              </label>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-slate-500">
                Enfermeras asignadas
              </Label>
              <p className="mt-0.5 text-[11px] text-slate-500">
                Toca un nombre para cambiar su estado: <strong>sin asignar</strong> &rarr; <strong className="text-green-700">activa</strong> &rarr; <strong className="text-amber-700">inactiva</strong> (conserva historial) &rarr; sin asignar.
                Las inactivas no podran crear nuevos programas.
              </p>
              {nurses.length === 0 ? (
                <p className="mt-1 text-xs text-slate-500">
                  Aun no hay enfermeras registradas. Creales cuenta primero en &quot;Gestionar enfermeras&quot;.
                </p>
              ) : (
                <ul className="mt-1 space-y-1" data-testid="patient-nurses-list">
                  {nurses.map((n) => {
                    const isAssigned = draft.assigned_nurse_ids.includes(n.id);
                    const isInactive = draft.inactive_nurse_ids.includes(n.id);
                    const state = !isAssigned ? "none" : isInactive ? "inactive" : "active";
                    const cfg = {
                      none:     { border: "border-slate-200 bg-white",   label: "Asignar",  badge: "bg-slate-100 text-slate-500" },
                      active:   { border: "border-green-500 bg-green-50", label: "Activa",   badge: "bg-green-600 text-white" },
                      inactive: { border: "border-amber-500 bg-amber-50", label: "Inactiva", badge: "bg-amber-600 text-white" },
                    }[state];
                    return (
                      <li key={n.id}>
                        <button
                          type="button"
                          onClick={() => toggleNurse(n.id)}
                          className={`flex w-full items-center justify-between gap-2 rounded-xl border-2 px-3 py-2 text-left text-sm transition-all ${cfg.border} hover:border-[#1D4ED8]`}
                          data-testid={`patient-nurse-toggle-${n.id}`}
                          title="Click para alternar: sin asignar -> activa -> inactiva (mantiene historial) -> sin asignar"
                        >
                          <span className="min-w-0">
                            <span className="font-bold text-slate-900">{n.name}</span>
                            <span className="ml-1 text-xs text-slate-500">{n.email}</span>
                          </span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${cfg.badge}`}
                            data-testid={`patient-nurse-state-${n.id}`}
                          >
                            {cfg.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-xl"
              data-testid="patient-dialog-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-xl bg-[#1D4ED8] font-semibold hover:bg-[#1E40AF]"
              data-testid="patient-dialog-save"
            >
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
              ) : editingId ? (
                "Actualizar"
              ) : (
                "Registrar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar paciente</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminara &quot;{deleteTarget?.name}&quot;. Los programas y el catalogo de medicamentos asociados NO se borran. Esta accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MedicationCatalogDialog
        open={!!medsPatient}
        onOpenChange={(o) => {
          if (!o) {
            setMedsPatient(null);
            setMedCatalog([]);
          }
        }}
        patientName={medsPatient?.name || ""}
        meds={medCatalog}
        reload={reloadMeds}
      />
    </div>
  );
}
